import React from "react";
import { ClassNameProps, Row, StyleProps } from "@hex-insights/core";
import { Icon } from "../../../../Components/Icon";
import { Statistic, statisticColors } from "../../../../Components/Statistics";
import { Tile } from "../../../../Components/Tile";
import {
	CalendarEvent,
	CalendarEventResponseResponse,
	CalendarEventStatistics,
	defaultCalendarEventStatistics,
	useCalendarEventStatisticsQuery,
} from "../../../../Utilities";
import * as CalendarEventResponseComponents from "../../CalendarEventResponse";

export type ControlledIndividualStatisticsTileProps = Omit<IndividualStatisticsTileProps, "isLoading" | "statistics">;

export function ControlledIndividualStatisticsTile({
	calendarEventID,
	...props
}: ControlledIndividualStatisticsTileProps) {
	const { loading, data } = useCalendarEventStatisticsQuery({
		variables: {
			filters: { idEQ: calendarEventID },
		},
		fetchPolicy: "cache-and-network",
	});

	return (
		<IndividualStatisticsTile
			calendarEventID={calendarEventID}
			isLoading={loading}
			statistics={data?.calendarEventStatistics}
			{...props}
		/>
	);
}

export type IndividualStatisticsTileProps = {
	calendarEventID: CalendarEvent["id"];
	isLoading?: boolean;
	statistics: CalendarEventStatistics | null | undefined;
} & Partial<ClassNameProps & StyleProps>;

export function IndividualStatisticsTile({
	calendarEventID,
	statistics: statisticsProp,
}: IndividualStatisticsTileProps) {
	const statistics = statisticsProp ?? defaultCalendarEventStatistics;

	const attendingFilters = React.useMemo(
		() => ({ calendarEventIDEQ: calendarEventID, responseEQ: CalendarEventResponseResponse.Attending }),
		[calendarEventID],
	);
	const maybeAttendingFilters = React.useMemo(
		() => ({ calendarEventIDEQ: calendarEventID, responseEQ: CalendarEventResponseResponse.MaybeAttending }),
		[calendarEventID],
	);
	const notAttendingFilters = React.useMemo(
		() => ({ calendarEventIDEQ: calendarEventID, responseEQ: CalendarEventResponseResponse.NotAttending }),
		[calendarEventID],
	);

	return (
		<Tile>
			<Tile.Body>
				<Row justify="spaced-start">
					<CalendarEventResponseComponents.UsersListModalController
						heading="Going"
						filters={attendingFilters}
						disabled={statistics.responsesAttending === 0}
					>
						<Statistic
							label="Going"
							value={statistics.responsesAttending}
							color={statisticColors.responseGoingColor}
							IconComponent={Icon.ThumbsUp}
							noGrammaticalNumber
						/>
					</CalendarEventResponseComponents.UsersListModalController>

					<CalendarEventResponseComponents.UsersListModalController
						heading="Not Going"
						filters={notAttendingFilters}
						disabled={statistics.responsesNotAttending === 0}
					>
						<Statistic
							label="Not Going"
							value={statistics.responsesNotAttending}
							color={statisticColors.responseNotGoingColor}
							IconComponent={Icon.ThumbsDown}
							noGrammaticalNumber
						/>
					</CalendarEventResponseComponents.UsersListModalController>

					<CalendarEventResponseComponents.UsersListModalController
						heading="Maybe"
						filters={maybeAttendingFilters}
						disabled={statistics.responsesMaybeAttending === 0}
					>
						<Statistic
							label="Maybe"
							value={statistics.responsesMaybeAttending}
							color={statisticColors.responseMaybeColor}
							IconComponent={Icon.Question}
							noGrammaticalNumber
						/>
					</CalendarEventResponseComponents.UsersListModalController>
				</Row>
			</Tile.Body>
		</Tile>
	);
}
